import { useRef, useEffect, useState, useCallback } from 'react';
import { X, Undo2, Trash2, Check, Loader2 } from 'lucide-react';

const COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Yellow', value: '#facc15' },
  { name: 'White', value: '#ffffff' },
  { name: 'Green', value: '#22c55e' },
];

// Cap the working resolution so annotated JPEGs stay small.
const MAX_DIM = 1600;

/**
 * Full-screen photo markup editor. Loads a photo onto a canvas, lets the
 * user draw freehand over it (to circle/arrow defects), then flattens the
 * drawing onto the image and returns a new JPEG data URL via onSave.
 */
const PhotoAnnotator = ({ src, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const strokesRef = useRef([]); // [{ color, width, points: [{x,y}] }]
  const drawingRef = useRef(false);

  const [color, setColor] = useState(COLORS[0].value);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [, forceRender] = useState(0); // re-render to toggle undo/clear disabled

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    for (const stroke of strokesRef.current) {
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.beginPath();
      stroke.points.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.stroke();
    }
  }, []);

  // Load the image and size the canvas to its (capped) natural resolution.
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      let { naturalWidth: w, naturalHeight: h } = img;
      const scale = Math.min(1, MAX_DIM / Math.max(w, h));
      w = Math.round(w * scale);
      h = Math.round(h * scale);
      const canvas = canvasRef.current;
      canvas.width = w;
      canvas.height = h;
      imgRef.current = img;
      setReady(true);
      redraw();
    };
    img.src = src;
  }, [src, redraw]);

  const strokeWidth = () => {
    const canvas = canvasRef.current;
    return canvas ? Math.max(3, Math.round(canvas.width * 0.008)) : 5;
  };

  const pointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0] || e;
    return {
      x: ((t.clientX - rect.left) / rect.width) * canvas.width,
      y: ((t.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    strokesRef.current.push({ color, width: strokeWidth(), points: [pointFromEvent(e)] });
    redraw();
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    stroke.points.push(pointFromEvent(e));
    redraw();
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    forceRender((n) => n + 1);
  };

  const undo = () => {
    strokesRef.current.pop();
    redraw();
    forceRender((n) => n + 1);
  };

  const clear = () => {
    strokesRef.current = [];
    redraw();
    forceRender((n) => n + 1);
  };

  const save = () => {
    setSaving(true);
    // ensure the latest strokes are painted, then export
    redraw();
    const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.85);
    onSave(dataUrl);
  };

  const hasStrokes = strokesRef.current.length > 0;

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900 flex flex-col animate-fade-in">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white flex-shrink-0">
        <button
          type="button"
          onClick={onCancel}
          className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center btn-touch"
          aria-label="Cancel"
        >
          <X className="w-5 h-5" />
        </button>
        <p className="text-sm font-semibold text-slate-200">Mark the issue</p>
        <button
          type="button"
          onClick={save}
          disabled={!ready || saving}
          className="px-4 h-10 rounded-xl bg-gradient-to-r from-brand-500 to-teal-500 font-bold flex items-center gap-2 btn-touch disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save
        </button>
      </div>

      {/* Canvas */}
      <div className="flex-1 min-h-0 flex items-center justify-center px-3">
        {!ready && <Loader2 className="w-8 h-8 text-white animate-spin" />}
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className={`max-w-full max-h-full rounded-xl shadow-2xl touch-none ${ready ? '' : 'hidden'}`}
          style={{ touchAction: 'none' }}
        />
      </div>

      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-4 safe-bottom">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setColor(c.value)}
                aria-label={c.name}
                className={`w-9 h-9 rounded-full border-2 transition-transform ${
                  color === c.value ? 'border-white scale-110' : 'border-white/30'
                }`}
                style={{ backgroundColor: c.value }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={undo}
              disabled={!hasStrokes}
              className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center btn-touch disabled:opacity-40"
              aria-label="Undo"
            >
              <Undo2 className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={!hasStrokes}
              className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center btn-touch disabled:opacity-40"
              aria-label="Clear"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoAnnotator;
